import { clamp } from "lodash";
import BetModel from "../../../vendor_core/core/src/model/BetModel";
import ViewManager from "../../../vendor_core/core/src/utils/ViewManager";
import BURReelsModel from "../models/BURReelsModel";
import { CoinColor } from "../types/BURGameTypes";
import { BitmapText } from "pixi.js";
import gsap from "gsap";
import App from "../../../vendor_core/core/src/App";
import BURFeatureModel from "../models/BURFeatureModel";

interface ChestThresholds {
    min: number;
    max: number;
    level: number;
}

export default class BUREnergyMetersView {
    //@ts-ignore
    private chestThresholds: Record<CoinColor, ChestThresholds[]>;
    //@ts-ignore
    private chestNumbers: Record<CoinColor, BitmapText> = {};
    private chestProgress: Record<CoinColor, number> = {
        Blue: 0,
        Red: 0,
        Yellow: 0
    };
    private chestLevels: Record<CoinColor, number> = {
        Blue: 1,
        Red: 1,
        Yellow: 1
    };
    private fontSizes = {
        big: 52,
        small: 42
    };

    public init() {
        Array.from(BURReelsModel.energy_coin_ids.keys()).forEach((color) => {
            const lcColor = color.toLowerCase();
            const scene = ViewManager.getLayer(
                `${color}Meter`
            ).getCurrentScene();

            const chestAnim = scene.spineAnimations.get(`${color}Chest`)!;

            chestAnim.setAnimationSkin(lcColor);

            const numberBitmap = new BitmapText("0", {
                fontName: `digits_${lcColor}`,
                letterSpacing: -10,
                fontSize: this.fontSizes.small
            });
            numberBitmap.anchor.set(0.5);
            chestAnim.addChild(numberBitmap);

            // Attach to the number bone
            const numberBone = chestAnim.skeleton.findBone(
                `number_${lcColor}`
            )!;

            App.instance.ticker.add(() => {
                numberBitmap.transform.setFromMatrix(numberBone.matrix);
            });

            this.chestNumbers[color] = numberBitmap;
        });

        this.chestThresholds = {
            Blue: [
                { min: 0, max: 0, level: 1 },
                { min: 1, max: 10, level: 2 },
                { min: 11, max: 20, level: 3 },
                { min: 21, max: 30, level: 4 },
                { min: 31, max: 40, level: 5 },
                { min: 41, max: 50, level: 6 },
                { min: 51, max: 60, level: 7 },
                { min: 61, max: 70, level: 8 },
                { min: 71, max: 80, level: 9 },
                {
                    min: 81,
                    max: BURReelsModel.meter_config!.blue.coinsMax,
                    level: 10
                }
            ],
            Yellow: [
                { min: 0, max: 0, level: 1 },
                { min: 1, max: 2, level: 2 },
                { min: 3, max: 4, level: 3 },
                { min: 5, max: 6, level: 4 },
                { min: 7, max: 8, level: 5 },
                { min: 9, max: 10, level: 6 },
                { min: 11, max: 12, level: 7 },
                { min: 13, max: 14, level: 8 },
                { min: 15, max: 16, level: 9 },
                {
                    min: 17,
                    max: BURReelsModel.meter_config!.yellow.coinsMax,
                    level: 10
                }
            ],
            Red: [
                { min: 0, max: 0, level: 1 },
                { min: 1, max: 1, level: 3 },
                { min: 2, max: 2, level: 6 },
                { min: 3, max: 3, level: 8 },
                {
                    min: BURReelsModel.meter_config!.red.coinsMax,
                    max: BURReelsModel.meter_config!.red.coinsMax,
                    level: 10
                }
            ]
        };
    }

    public onBetChanged(currentBet = BetModel.totalBet) {
        const betState = BURReelsModel.meter_state.find(
            (s) => Number(s.bet) === currentBet
        );
        const cfg = BURReelsModel.meter_config!;

        const progress: Record<CoinColor, number> = {
            Blue: 0,
            Yellow: 0,
            Red: 0
        };

        if (betState) {
            progress.Red =
                (betState.data.wildCount - cfg.red.value.start) /
                cfg.red.value.step;
            progress.Blue =
                (betState.data.blueMeter - cfg.blue.value.start) /
                cfg.blue.value.step;
            progress.Yellow = betState.data.yellowCoins;

            this.setChestNumber("Blue", progress.Blue);
            this.setChestNumber("Red", progress.Red);
            this.setChestNumber("Yellow", progress.Yellow);
        }

        (Object.keys(progress) as CoinColor[]).map((color) => {
            this.chestProgress[color] = progress[color];
            this.playChestIdleAnim(color);
        });
    }

    public async onFreeSpinsExit() {
        await Promise.all(
            BURFeatureModel.free_spins_data!.combo.map(async (color) => {
                if (this.chestProgress[color] === 0) return;

                this.chestProgress[color] = 0;
                this.updateChestNumber(color);

                if (this.chestLevels[color] <= 1) return;

                const scene = ViewManager.getLayer(
                    `${color}Meter`
                ).getCurrentScene();
                const energyChest = scene.spineAnimations.get(`${color}Chest`)!;
                await energyChest.playAsync(
                    `reset_level_${this.chestLevels[color]}`
                );
                this.chestLevels[color] = 1;
            })
        );
    }

    /**
     * Collects an energy coin in the respective chest.
     * @param color color of the energy meter
     */
    public async collectCoin(color: CoinColor) {
        let currentProgress = this.chestProgress[color];
        const thresholds = this.chestThresholds[color];
        const maxProgress = thresholds[thresholds.length - 1].max;

        currentProgress = clamp(currentProgress + 1, 0, maxProgress);

        if (currentProgress === this.chestProgress[color]) return;

        // Update the chest progress
        this.chestProgress[color] = currentProgress;

        // Update the chest number
        // If new chest level has been reached, play the transition
        await Promise.all([
            this.updateChestNumber(color),
            this.checkForLevelUp(color)
        ]);
    }

    private async checkForLevelUp(color: CoinColor) {
        const currentProgress = this.chestProgress[color];
        const levelUp = this.chestThresholds[color].find(
            (t) => t.min === currentProgress
        );
        if (levelUp) {
            await this.playLevelUp(color, levelUp);
        }
    }

    private async playLevelUp(color: CoinColor, level: ChestThresholds) {
        const scene = ViewManager.getLayer(`${color}Meter`).getCurrentScene();
        const energyChest = scene.spineAnimations.get(`${color}Chest`)!;
        const currentLevel = this.chestLevels[color];
        const newLevel = level.level;
        const jumpingLevels = newLevel - currentLevel > 1;
        energyChest.clearState();
        await energyChest.playAsync(
            `level${newLevel - 1}_to_${newLevel}`,
            2,
            false,
            jumpingLevels ? 0.5 : 0 // If the levels are not consecutive (like the red coin level ups), start the animation from the middle to avoid first frame jumps
        );
        this.chestLevels[color] = level.level;
        this.playChestIdleAnim(color);
    }

    /**
     * Manually set chests progress
     * To be used only in recover state
     * @param color
     * @param difference
     */
    public onRecover(color: CoinColor, difference: number) {
        let progress = this.chestProgress[color];
        const thresholds = this.chestThresholds[color];
        const maxProgress = thresholds[thresholds.length - 1].max;

        progress = clamp(progress + difference, 0, maxProgress);
        this.chestProgress[color] = progress;
        this.setChestNumber(color, progress);
        this.playChestIdleAnim(color);
    }

    private playChestIdleAnim(color: CoinColor) {
        const scene = ViewManager.getLayer(`${color}Meter`).getCurrentScene();
        const energyChest = scene.spineAnimations.get(`${color}Chest`)!;
        const chestProgress = this.chestProgress[color];
        const chestLevel = this.chestThresholds[color].find(
            (t) => chestProgress >= t.min && chestProgress <= t.max
        )!.level;
        energyChest.clearState();
        energyChest.play(`chest_level${chestLevel}_idle`);
    }

    private setChestNumber(color: CoinColor, number: number) {
        this.chestNumbers[color].text = number.toString();
        this.chestNumbers[color].fontSize =
            number <= 9 ? this.fontSizes.big : this.fontSizes.small;
    }

    private async updateChestNumber(color: CoinColor) {
        const progress = this.chestProgress[color];
        const num = this.chestNumbers[color];
        await gsap.to(num, {
            pixi: { brightness: 1.5 },
            duration: 0.15,
            ease: "none",
            repeat: 1,
            yoyo: true,
            onRepeat: () => {
                num.text = progress.toString();
                num.fontSize =
                    progress <= 9 ? this.fontSizes.big : this.fontSizes.small;
            }
        });
    }
}
